import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TablePagination,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
  AttachMoney as MoneyIcon,
  AccessTime as PendingIcon,
  VerifiedUser as ApprovedIcon,
  Error as ErrorIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import {
  updateScreenTitle,
  showSuccessMessage,
  showErroMessage,
  showLoader,
  dismissLoader
} from '../../../../LayoutContainers/AppLayout/redux/AppSlice';
import MockDataIndicator from '../../../../Components/Common/MockDataIndicator';
import WDButton from '../../../../MaterialComponents/WDButton';
import AppService from '../../../../Configurations/Appservice';
import pxToRem from '../../../../Themes/Functions/pxToRem';
import apiClient from '../../../../services/apiClient';
import API_ENDPOINTS from '../../../../Api/endpoints';

// ============================================
// MOCK DATA FOR TESTING
// ============================================

const MOCK_PENDING_APPROVALS = [
  {
    transactionId: 'a1b2c3d4-e5f6-4789-0abc-def123456789',
    id: 'topup-001',
    customerName: 'Ahmed Al-Mansouri',
    customerId: 'org-001',
    amount: 1250.00,
    currency: 'AED',
    paymentMethod: 'BANK_TRANSFER',
    destinationBank: 'Emirates Islamic Bank',
    status: 'PENDING',
    initiatedAt: '2025-11-15T10:30:00Z',
    transactionDate: '2025-11-15',
    proofDocumentPath: null,
    initiatedBy: 'Customer Self',
    bankReference: 'BT-20251115-001',
    description: 'Top-up request for service payment',
    notes: 'Urgent approval needed'
  },
  {
    transactionId: 'b2c3d4e5-f6g7-8901-1bcd-ef1234567890',
    id: 'topup-002',
    customerName: 'Fatima Al-Habsi',
    customerId: 'org-002',
    amount: 850.00,
    currency: 'AED',
    paymentMethod: 'BANK_TRANSFER',
    destinationBank: 'National Bank of Dubai',
    status: 'PENDING',
    initiatedAt: '2025-11-15T09:15:00Z',
    transactionDate: '2025-11-15',
    proofDocumentPath: null,
    initiatedBy: 'Customer Self',
    bankReference: 'BT-20251115-002',
    description: 'Wallet top-up',
    notes: ''
  },
  {
    transactionId: 'c3d4e5f6-g7h8-9012-2cde-f12345678901',
    id: 'topup-003',
    customerName: 'Mohammed Hassan',
    customerId: 'org-003',
    amount: 2500.00,
    currency: 'AED',
    paymentMethod: 'BANK_TRANSFER',
    destinationBank: 'FAB - First Abu Dhabi Bank',
    status: 'PENDING',
    initiatedAt: '2025-11-15T08:45:00Z',
    transactionDate: '2025-11-15',
    proofDocumentPath: null,
    initiatedBy: 'Customer Self',
    bankReference: 'BT-20251115-003',
    description: 'Multiple ticket payment',
    notes: 'High priority customer'
  }
];

// ============================================
// PAYMENT APPROVALS DASHBOARD COMPONENT
// ============================================

export default function PaymentApprovals() {
  const dispatch = useDispatch();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [filteredApprovals, setFilteredApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);
  const [updateBankDialogOpen, setUpdateBankDialogOpen] = useState(false);
  const [selectedBank, setSelectedBankValue] = useState('');
  const [bankComment, setBankComment] = useState('');
  const [updateBankLoading, setUpdateBankLoading] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [proofLoading, setProofLoading] = useState(false);
  const [proofError, setProofError] = useState(null);
  const [stats, setStats] = useState({
    totalPending: 0,
    totalAmount: 0,
    oldestRequest: null
  });

  // Load pending approvals on mount
  useEffect(() => {
    dispatch(updateScreenTitle('Payment Approvals Dashboard'));
    loadPendingApprovals();
    loadBankAccounts();
  }, [dispatch]);

  // Filter approvals based on search term
  useEffect(() => {
    const filtered = pendingApprovals.filter(approval =>
      approval.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      approval.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (approval.transactionNumber && approval.transactionNumber.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredApprovals(filtered);
    setPage(0); // Reset pagination on search
  }, [searchTerm, pendingApprovals]);

  // Calculate statistics
  useEffect(() => {
    if (pendingApprovals.length > 0) {
      const total = pendingApprovals.reduce((sum, item) => sum + item.amount, 0);
      const oldest = pendingApprovals.reduce((min, item) => {
        const itemDate = item.transactionDate || item.initiatedAt;
        const minDate = min.transactionDate || min.initiatedAt;
        return new Date(itemDate) < new Date(minDate) ? item : min;
      });

      setStats({
        totalPending: pendingApprovals.length,
        totalAmount: total,
        oldestRequest: oldest
      });
    } else {
      setStats({
        totalPending: 0,
        totalAmount: 0,
        oldestRequest: null
      });
    }
  }, [pendingApprovals]);

  // Transform legacy TopupRequest API response to component format
  const transformLegacyTopupRequests = useCallback((apiData) => {
    if (!Array.isArray(apiData)) return [];

    return apiData.map((payment) => {
      // Extract tags if available
      const tags = payment.tags || {};
      const isTypingCenterPayment = tags.source === 'TYPING_CENTER';

      return {
        // Core fields from API response
        transactionId: payment.id, // Use topup request ID as transaction ID
        id: payment.id,
        transactionNumber: payment.transactionNumber || 'N/A', // Actual transaction number from API
        referenceNumber: payment.transactionNumber || (payment.id ? `REF-${payment.id.substring(0, 5)}` : ''), // Use transactionNumber as reference
        customerName: payment.organisation || 'N/A', // organisation field
        amount: typeof payment.amount === 'string' ? parseFloat(payment.amount) : (payment.amount || 0),
        paymentMethod: payment.paymentMethod || 'BANK_TRANSFER', // paymentMethod field
        status: payment.status || 'PENDING',
        sourceBank: payment.customerPaymentMethod || 'N/A', // Source: customer's payment method (HDFC, etc.)
        destinationBank: payment.destinationBank || 'Not Assigned', // Destination: Thaklis' bank account (NBD, ICICI, FAB)
        destinationBankAccountId: payment.destinationBankAccountId || null, // Destination bank account ID
        initiatedAt: payment.createDateTime || new Date().toISOString(), // createDateTime field
        transactionDate: payment.transactionDate || null, // transactionDate field (YYYY-MM-DD)
        proofDocumentPath: payment.proofDocumentPath || payment.proofDocumentPathS3 || payment.proofPath || null,

        // Generated/derived fields for UI compatibility
        currency: 'AED', // Default to AED
        bankReference: payment.transactionNumber || (payment.id ? `REF-${payment.id.substring(0, 5)}` : 'N/A'), // Use transactionNumber as bank reference
        description: `${payment.paymentMethod || 'Bank Transfer'} Payment`,
        notes: '', // Legacy API doesn't have notes
        initiatedBy: payment.requestedBy || 'Customer Self', // requestedBy field

        // Payment Source (Typing Center vs Direct Customer)
        isTypingCenterPayment,
        paymentSource: isTypingCenterPayment ? 'TYPING_CENTER' : 'DIRECT',
        typingCenterId: tags.typingCenterId || null,
        typingCenterName: tags.typingCenterName || null,
        typingCenterCode: tags.typingCenterCode || null,
        invoiceNumber: tags.invoiceNumber || null,
        invoiceId: tags.invoiceId || null,
        paymentReference: tags.paymentReference || null,
        receiptUrl: tags.receiptUrl || null,
        receiptFilename: tags.receiptFilename || null,
        ticketIds: tags.ticketIds ? JSON.parse(tags.ticketIds) : [],
        ticketCount: tags.ticketCount ? parseInt(tags.ticketCount, 10) : 0,
        ticketTotal: tags.ticketTotal ? parseFloat(tags.ticketTotal) : 0,
        walletCredit: tags.walletCredit ? parseFloat(tags.walletCredit) : 0,
        syncedAt: tags.syncedAt || null,
        syncedBy: tags.syncedBy || null,

        // Amount-mismatch flag — set by the customer-side Payments page when the
        // submitted amount differs from the amount extracted from the uploaded
        // payment proof. Surfaced on the table so admins notice before approving.
        amountMismatch: tags.amountMismatch === 'true',
        extractedAmount: tags.extractedAmount ? parseFloat(tags.extractedAmount) : null,
        submittedAmount: tags.submittedAmount ? parseFloat(tags.submittedAmount) : null,

        // Additional fields for compatibility
        sourceType: 'LEGACY', // Mark as legacy topup request
        confirmedAt: null,
        reconciledAt: null,
        completedAt: null,
        confirmedBy: null,
        reconciledBy: null,
        externalTransactionId: null,
        failureReason: null,
        progress: 0
      };
    });
  }, []);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleViewProof = async () => {
    try {
      if (!selectedPayment?.proofDocumentPath) return;
      setProofError(null);
      setProofLoading(true);
      const url = API_ENDPOINTS.FILES.DOWNLOAD(selectedPayment.proofDocumentPath);
      // FILES.DOWNLOAD is already an absolute path (includes the API base /proxy);
      // override baseURL so apiClient doesn't prepend /proxy a second time.
      const resp = await apiClient.get(url, { responseType: 'blob', baseURL: '' });
      const blobUrl = window.URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Proof view failed', e);
      setProofError('Failed to open proof document. Please try again.');
    } finally {
      setProofLoading(false);
    }
  };

  const handleDownloadProof = async () => {
    try {
      if (!selectedPayment?.proofDocumentPath) return;
      setProofError(null);
      setProofLoading(true);
      const url = API_ENDPOINTS.FILES.DOWNLOAD(selectedPayment.proofDocumentPath);
      // Absolute path already includes the API base /proxy — don't double-prefix.
      const resp = await apiClient.get(url, { responseType: 'blob', baseURL: '' });
      downloadBlob(resp.data, `topup-proof-${selectedPayment.transactionNumber || selectedPayment.transactionId || 'document'}`);
    } catch (e) {
      console.error('Proof download failed', e);
      setProofError('Failed to download proof document. Please try again.');
    } finally {
      setProofLoading(false);
    }
  };

  const loadPendingApprovals = useCallback(async () => {
    setLoading(true);
    try {
      if (useMockData) {
        // Use mock data for testing/demo
        setPendingApprovals(MOCK_PENDING_APPROVALS);
        dispatch(showSuccessMessage('Loaded mock pending approvals'));
      } else {
        // Use the legacy topup request endpoint to show ALL pending payments
        // This includes payments from the old system (t_topup_request table)
        dispatch(showLoader());
        const response = await AppService.get('/v1/topupRequest/pending');

        if (response && Array.isArray(response)) {
          // Legacy API returns an array of TopUpRequestResponse objects
          const transformedData = transformLegacyTopupRequests(response);
          setPendingApprovals(transformedData);
          dispatch(showSuccessMessage(`Loaded ${transformedData.length} pending approvals`));
        } else {
          setPendingApprovals([]);
          dispatch(showErroMessage('Failed to load pending approvals'));
        }
        dispatch(dismissLoader());
      }
    } catch (error) {
      console.error('Error loading pending approvals:', error);
      dispatch(showErroMessage('Error loading pending approvals'));
      setPendingApprovals([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, useMockData]);

  const loadBankAccounts = useCallback(async () => {
    try {
      console.log('[PaymentApprovals] Fetching bank accounts from API...');
      const response = await AppService.get('/v1/admin/bank-accounts');

      if (response && Array.isArray(response)) {
        console.log('[PaymentApprovals] Loaded', response.length, 'bank accounts');
        setBankAccounts(response);
      } else {
        console.log('[PaymentApprovals] No bank accounts returned from API');
        setBankAccounts([]);
      }
    } catch (error) {
      console.error('[PaymentApprovals] Error loading bank accounts:', error);
      // Set to empty, don't fail the component
      setBankAccounts([]);
    }
  }, []);

  const handleConfirmPayment = async () => {
    if (!selectedPayment) return;

    setConfirmLoading(true);
    try {
      dispatch(showLoader());

      if (useMockData) {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Remove from pending list
        const updated = pendingApprovals.filter(
          p => p.transactionId !== selectedPayment.transactionId
        );
        setPendingApprovals(updated);

        dispatch(showSuccessMessage(`Payment ${selectedPayment.transactionId} confirmed successfully`));
      } else {
        // Call legacy topup request approval API
        const response = await AppService.put(
          `/v1/topupRequest/approve?requestId=${selectedPayment.transactionId}`
        );

        if (response) {
          // Remove confirmed payment from list
          const updated = pendingApprovals.filter(
            p => p.transactionId !== selectedPayment.transactionId
          );
          setPendingApprovals(updated);

          dispatch(showSuccessMessage('Payment approved successfully'));
        } else {
          dispatch(showErroMessage('Failed to approve payment'));
        }
      }

      setConfirmDialogOpen(false);
      setApprovalNotes('');
      setSelectedPayment(null);
    } catch (error) {
      console.error('Error approving payment:', error);
      dispatch(showErroMessage('Error approving payment'));
    } finally {
      setConfirmLoading(false);
      dispatch(dismissLoader());
    }
  };

  const handleOpenDetails = (payment) => {
    setProofLoading(false);
    setProofError(null);
    setSelectedPayment(payment);
    setDetailsDialogOpen(true);
  };

  const handleOpenConfirm = (payment) => {
    setSelectedPayment(payment);
    setApprovalNotes('');
    setConfirmDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setRejectDialogOpen(false);
    setUpdateBankDialogOpen(false);
    setProofLoading(false);
    setProofError(null);
    setRejectReason('');
    setSelectedBankValue('');
    setBankComment('');
    setSelectedPayment(null);
  };

  const handleCloseConfirm = () => {
    setConfirmDialogOpen(false);
    setApprovalNotes('');
    setSelectedPayment(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = () => {
    // Export pending approvals to CSV
    const headers = ['Transaction ID', 'Customer', 'Amount', 'Bank', 'Status', 'Transaction Date'];
    const rows = filteredApprovals.map(p => [
      p.transactionId,
      p.customerName,
      `${p.currency} ${p.amount}`,
      p.destinationBank,
      p.status,
      p.transactionDate || p.initiatedAt
    ]);

    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-approvals-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();

    dispatch(showSuccessMessage('Exported to CSV'));
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      PENDING: { label: 'Pending', color: 'warning', icon: <PendingIcon /> },
      CONFIRMED: { label: 'Confirmed', color: 'success', icon: <CheckIcon /> },
      FAILED: { label: 'Failed', color: 'error', icon: <ErrorIcon /> }
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return <Chip label={config.label} color={config.color} variant="outlined" size="small" />;
  };

  const formatCurrency = (amount, currency = 'AED') => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTransactionDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Paginate filtered results
  const paginatedApprovals = filteredApprovals.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: pxToRem(20) }}>
      <MockDataIndicator visible={useMockData} />

      {/* Header with Title and Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: pxToRem(20) }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            💳 Payment Approvals Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: pxToRem(5) }}>
            Approve or review pending payment transactions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: pxToRem(10) }}>
          <Tooltip title="Refresh data">
            <IconButton onClick={loadPendingApprovals} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <WDButton
            onClick={handleExportCSV}
            disabled={filteredApprovals.length === 0}
            startIcon={<DownloadIcon />}
          >
            Export CSV
          </WDButton>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: pxToRem(25) }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Pending Approvals
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, mt: pxToRem(8) }}>
                    {stats.totalPending}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: pxToRem(40), color: '#ff9800' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Pending Amount
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, mt: pxToRem(8) }}>
                    {formatCurrency(stats.totalAmount)}
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: pxToRem(40), color: '#2196f3' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Oldest Request
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: pxToRem(8), color: '#d32f2f' }}>
                    {stats.oldestRequest
                      ? formatTransactionDate(stats.oldestRequest.transactionDate || stats.oldestRequest.initiatedAt)
                      : 'N/A'}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: pxToRem(40), color: '#d32f2f' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Filter Status
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: pxToRem(8) }}>
                    Showing {paginatedApprovals.length} of {filteredApprovals.length}
                  </Typography>
                </Box>
                <ApprovedIcon sx={{ fontSize: pxToRem(40), color: '#4caf50' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Card sx={{ mb: pxToRem(20) }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by customer name, transaction ID, or transaction number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">🔍</InputAdornment>
            }}
            size="small"
          />
          <Typography variant="caption" sx={{ mt: pxToRem(10), display: 'block', color: '#666' }}>
            {searchTerm && `Found ${filteredApprovals.length} matching approvals`}
          </Typography>
        </CardContent>
      </Card>

      {/* Pending Approvals Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: pxToRem(40) }}>
              <CircularProgress />
            </Box>
          ) : filteredApprovals.length === 0 ? (
            <Alert severity="info" sx={{ m: pxToRem(15) }}>
              No pending approvals at this time. Great job staying on top of payments! ✨
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#006B63', color: 'white', height: '56px' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Transaction ID</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Customer</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Amount</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Source Bank</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Destination Bank</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Transaction Date</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedApprovals.map((payment) => (
                      <TableRow
                        key={payment.transactionId}
                        sx={{
                          // Red tint for topups where the customer-submitted amount
                          // doesn't match the amount extracted from the uploaded proof.
                          backgroundColor: payment.amountMismatch ? '#ffebee' : 'inherit',
                          '&:hover': {
                            backgroundColor: payment.amountMismatch ? '#ffcdd2' : '#f5f5f5',
                          },
                          height: '56px'
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: payment.amountMismatch ? '#c62828' : 'inherit' }}>
                          {payment.transactionNumber || payment.transactionId.substring(0, 8)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: payment.amountMismatch ? '#c62828' : 'inherit' }}>
                          {payment.customerName}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.875rem', fontWeight: 500, color: payment.amountMismatch ? '#c62828' : 'inherit' }}>
                          {payment.amountMismatch ? (
                            <Tooltip
                              arrow
                              title={
                                <Box>
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>
                                    Amount differs from uploaded proof
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.7rem' }}>
                                    Extracted from proof: AED {payment.extractedAmount != null ? payment.extractedAmount.toFixed(2) : '—'}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.7rem' }}>
                                    Submitted by customer: AED {payment.submittedAmount != null ? payment.submittedAmount.toFixed(2) : payment.amount.toFixed(2)}
                                  </Typography>
                                </Box>
                              }
                            >
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                                <ErrorIcon sx={{ fontSize: 16, color: '#c62828' }} />
                                {formatCurrency(payment.amount, payment.currency)}
                              </Box>
                            </Tooltip>
                          ) : (
                            formatCurrency(payment.amount, payment.currency)
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {payment.sourceBank}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {payment.destinationBank}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {getStatusChip(payment.status)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {formatTransactionDate(payment.transactionDate || payment.initiatedAt)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDetails(payment)}
                              sx={{ color: '#006B63', '&:hover': { backgroundColor: 'rgba(0, 107, 99, 0.08)' } }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredApprovals.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      {selectedPayment && (
        <Dialog open={detailsDialogOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', bgcolor: '#006B63', color: 'white', mb: 0 }}>
            Payment Details - {selectedPayment.transactionNumber || selectedPayment.transactionId}
          </DialogTitle>
          <DialogContent sx={{ pt: pxToRem(20) }}>
            <Grid container spacing={2}>
              {/* Left Column */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Transaction Number</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.95rem', mt: 0.5, fontWeight: 600, color: '#006B63' }}>{selectedPayment.transactionNumber || 'N/A'}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Customer</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', mt: 0.5 }}>{selectedPayment.customerName}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Amount</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2196f3', fontSize: '1.2rem', mt: 0.5 }}>
                    {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Destination Bank</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', mt: 0.5 }}>{selectedPayment.destinationBank}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Thaklis Bank Account</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.95rem', mt: 0.5, fontFamily: 'monospace', fontWeight: 500 }}>Thaklis Bank - Main Account</Typography>
                </Box>
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} sm={6}>
                {/* Payment Source - Typing Center or Direct */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Payment Source</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {selectedPayment.isTypingCenterPayment ? (
                      <Chip
                        label="VIA TYPING CENTER"
                        size="small"
                        sx={{
                          bgcolor: '#E8F5E9',
                          color: '#2E7D32',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    ) : (
                      <Chip
                        label="DIRECT CUSTOMER"
                        size="small"
                        sx={{
                          bgcolor: '#E3F2FD',
                          color: '#1565C0',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Typing Center Details (if applicable) */}
                {selectedPayment.isTypingCenterPayment && selectedPayment.typingCenterName && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: '#E8F5E9', borderRadius: 1, border: '1px solid #C8E6C9' }}>
                    <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase' }}>Typing Center</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25, color: '#1B5E20' }}>
                      {selectedPayment.typingCenterName}
                      {selectedPayment.typingCenterCode && ` (${selectedPayment.typingCenterCode})`}
                    </Typography>
                    {selectedPayment.invoiceNumber && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#388E3C' }}>
                        Invoice: {selectedPayment.invoiceNumber}
                      </Typography>
                    )}
                    {selectedPayment.ticketCount > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#388E3C' }}>
                        Tickets: {selectedPayment.ticketCount} | Total: AED {selectedPayment.ticketTotal?.toFixed(2) || '0.00'}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Description</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.95rem', mt: 0.5 }}>{selectedPayment.description}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Transaction Date</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.95rem', mt: 0.5 }}>{formatTransactionDate(selectedPayment.transactionDate || selectedPayment.initiatedAt)}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusChip(selectedPayment.status)}
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    Proof Document
                  </Typography>

                  {!selectedPayment.proofDocumentPath ? (
                    <Typography variant="body2" sx={{ mt: 0.75, color: '#666' }}>
                      No uploaded proof document found.
                    </Typography>
                  ) : (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ViewIcon />}
                        onClick={handleViewProof}
                        disabled={proofLoading}
                        sx={{
                          textTransform: 'none',
                          color: '#006B63',
                          borderColor: '#006B63',
                          '& .MuiButton-startIcon': { color: '#006B63' },
                          '&:hover': { bgcolor: 'rgba(0, 107, 99, 0.04)', borderColor: '#006B63' },
                        }}
                      >
                        View Proof
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadProof}
                        disabled={proofLoading}
                        sx={{
                          textTransform: 'none',
                          color: '#006B63',
                          borderColor: '#006B63',
                          '& .MuiButton-startIcon': { color: '#006B63' },
                          '&:hover': { bgcolor: 'rgba(0, 107, 99, 0.04)', borderColor: '#006B63' },
                        }}
                      >
                        Download Proof
                      </Button>
                      {proofLoading && <CircularProgress size={18} />}
                    </Box>
                  )}

                  {proofError && (
                    <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setProofError(null)}>
                      {proofError}
                    </Alert>
                  )}
                </Box>
              </Grid>
            </Grid>

            {selectedPayment.notes && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Notes</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.9rem', mt: 0.5 }}>{selectedPayment.notes}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={handleCloseDetails} sx={{ color: '#666' }}>Close</Button>
            <Button
              onClick={() => {
                setDetailsDialogOpen(false);
                setUpdateBankDialogOpen(true);
              }}
              variant="outlined"
              startIcon={<EditIcon />}
              size="small"
              sx={{ borderColor: '#ff9800', color: '#ff9800', '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.04)', borderColor: '#ff9800' } }}
            >
              Update Bank
            </Button>
            <Button
              onClick={() => {
                setDetailsDialogOpen(false);
                setRejectDialogOpen(true);
              }}
              variant="outlined"
              startIcon={<RejectIcon />}
              size="small"
              sx={{ borderColor: '#f44336', color: '#f44336', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.04)', borderColor: '#f44336' } }}
            >
              Reject
            </Button>
            <WDButton
              onClick={() => {
                setDetailsDialogOpen(false);
                handleOpenConfirm(selectedPayment);
              }}
              startIcon={<ApproveIcon />}
              sx={{ bgcolor: '#006B63', '&:hover': { bgcolor: '#004d47' } }}
            >
              Approve
            </WDButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Confirm Approval Dialog */}
      {selectedPayment && (
        <Dialog
          open={confirmDialogOpen}
          onClose={(_, reason) => {
            if (confirmLoading) return;
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            handleCloseConfirm();
          }}
          disableEscapeKeyDown={confirmLoading}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, color: '#2196f3' }}>
            ✓ Confirm Payment Approval
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: pxToRem(15), mt: pxToRem(10) }}>
              You are about to approve this payment. This action cannot be undone.
            </Alert>

            <Box sx={{ mb: pxToRem(15) }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: pxToRem(10) }}>
                Payment Summary:
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: pxToRem(10) }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Customer:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedPayment.customerName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: pxToRem(10) }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Amount:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2196f3' }}>
                      {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Bank:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedPayment.destinationBank}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <TextField
                fullWidth
                label="Approval Notes (Optional)"
                multiline
                rows={3}
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add any notes about this approval..."
                variant="outlined"
                size="small"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirm} disabled={confirmLoading}>Cancel</Button>
            <WDButton
              onClick={handleConfirmPayment}
              loading={confirmLoading}
              startIcon={<CheckIcon />}
            >
              Confirm Approval
            </WDButton>
          </DialogActions>
          {confirmLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.6)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                zIndex: 1,
              }}
            >
              <CircularProgress />
              <Typography sx={{ fontSize: '0.9rem', color: '#444', fontWeight: 500 }}>
                Approving payment… please wait
              </Typography>
            </Box>
          )}
        </Dialog>
      )}

      {/* Reject Dialog */}
      {selectedPayment && (
        <Dialog
          open={rejectDialogOpen}
          onClose={(_, reason) => {
            if (rejectLoading) return;
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            setRejectDialogOpen(false);
            setRejectReason('');
          }}
          disableEscapeKeyDown={rejectLoading}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', bgcolor: '#f44336', color: 'white', mb: 0 }}>
            Reject Payment
          </DialogTitle>
          <DialogContent sx={{ pt: pxToRem(20) }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are about to reject this payment request. Please provide a reason.
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {selectedPayment.customerName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Amount: {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Rejection Reason"
              multiline
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a detailed reason for rejecting this payment..."
              variant="outlined"
              size="small"
              required
              autoFocus
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectReason('');
              }}
              disabled={rejectLoading}
              sx={{ color: '#666' }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!selectedPayment || !rejectReason.trim()) return;

                setRejectLoading(true);
                try {
                  dispatch(showLoader());

                  if (useMockData) {
                    // Simulate API call
                    await new Promise(resolve => setTimeout(resolve, 1000));

                    // Remove from pending list
                    const updated = pendingApprovals.filter(
                      p => p.transactionId !== selectedPayment.transactionId
                    );
                    setPendingApprovals(updated);

                    dispatch(showSuccessMessage(`Payment ${selectedPayment.transactionId} rejected successfully`));
                  } else {
                    // Call topup request reject API
                    const response = await AppService.put(
                      `/v1/topupRequest/reject?requestId=${selectedPayment.transactionId}`
                    );

                    if (response) {
                      // Remove rejected payment from list
                      const updated = pendingApprovals.filter(
                        p => p.transactionId !== selectedPayment.transactionId
                      );
                      setPendingApprovals(updated);

                      dispatch(showSuccessMessage('Payment rejected successfully'));
                    } else {
                      dispatch(showErroMessage('Failed to reject payment'));
                    }
                  }

                  setRejectDialogOpen(false);
                  setRejectReason('');
                  setDetailsDialogOpen(false);
                  setSelectedPayment(null);
                } catch (error) {
                  console.error('Error rejecting payment:', error);
                  dispatch(showErroMessage('Error rejecting payment'));
                } finally {
                  setRejectLoading(false);
                  dispatch(dismissLoader());
                }
              }}
              variant="contained"
              startIcon={<RejectIcon />}
              disabled={!rejectReason.trim() || rejectLoading}
              sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' } }}
            >
              {rejectLoading ? 'Rejecting...' : 'Confirm Rejection'}
            </Button>
          </DialogActions>
          {rejectLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.6)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                zIndex: 1,
              }}
            >
              <CircularProgress />
              <Typography sx={{ fontSize: '0.9rem', color: '#444', fontWeight: 500 }}>
                Rejecting payment… please wait
              </Typography>
            </Box>
          )}
        </Dialog>
      )}

      {/* Update Bank Dialog */}
      {selectedPayment && (
        <Dialog
          open={updateBankDialogOpen}
          onClose={() => {
            setUpdateBankDialogOpen(false);
            setSelectedBankValue('');
            setBankComment('');
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(14, 107, 107, 0.15)'
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', bgcolor: '#0E6B6B', color: 'white', mb: 0, py: 2.5 }}>
            Update Bank Details
          </DialogTitle>
          <DialogContent sx={{ pt: pxToRem(24), pb: pxToRem(16) }}>
            <Alert
              severity="info"
              sx={{
                mb: 3,
                bgcolor: '#E0F2F1',
                color: '#004D40',
                '& .MuiAlert-icon': { color: '#0E6B6B' },
                '& .MuiAlert-message': { fontSize: '0.9rem', fontWeight: 500 },
                borderRadius: 1.5,
                border: '1px solid #B2DFDB'
              }}
            >
              Update bank details for payment received from different bank.
            </Alert>

            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: '#0E6B6B',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Current Bank
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mt: 0.75,
                  color: '#1a1a1a',
                  fontSize: '1rem'
                }}
              >
                {selectedPayment.destinationBank}
              </Typography>
            </Box>

            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <InputLabel
                sx={{
                  fontSize: '0.9rem',
                  '&.Mui-focused': { color: '#0E6B6B' }
                }}
              >
                Select New Bank
              </InputLabel>
              <Select
                value={selectedBank}
                onChange={(e) => setSelectedBankValue(e.target.value)}
                label="Select New Bank"
                size="small"
                sx={{
                  fontSize: '0.95rem',
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#0E6B6B'
                    }
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 12px'
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.95rem' }}>-- Select Bank --</MenuItem>
                {bankAccounts && bankAccounts.length > 0 ? (
                  bankAccounts
                    .filter(bank => bank.type === 'OPERATIONS')
                    .map((bank) => (
                      <MenuItem key={bank.id} value={bank.id} sx={{ fontSize: '0.95rem', py: 1, fontWeight: 500 }}>
                        {bank.bankName}
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem disabled sx={{ fontSize: '0.95rem' }}>No banks available</MenuItem>
                )}
              </Select>
            </FormControl>

            {selectedBank && (
              <Box sx={{ mb: 2.5, p: 2, bgcolor: '#E0F2F1', borderRadius: 1.5, border: '1px solid #B2DFDB' }}>
                {(() => {
                  const selectedBankObj = bankAccounts.find(b => b.id === selectedBank);
                  return (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#0E6B6B',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'block',
                          mb: 0.75
                        }}
                      >
                        Bank Type
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#0E6B6B',
                          fontSize: '0.95rem'
                        }}
                      >
                        OPERATIONS
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            )}

            <TextField
              fullWidth
              label="Comment/Reason for Update"
              multiline
              rows={3}
              value={bankComment}
              onChange={(e) => setBankComment(e.target.value)}
              placeholder="Please explain why the bank details are being updated..."
              variant="outlined"
              size="small"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#0E6B6B'
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  opacity: 0.6
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: '1px solid #E8E8E8' }}>
            <Button
              onClick={() => {
                setUpdateBankDialogOpen(false);
                setSelectedBankValue('');
                setBankComment('');
              }}
              sx={{
                color: '#666',
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#F5F5F5'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!selectedBank || !bankComment.trim() || !selectedPayment) return;

                setUpdateBankLoading(true);
                try {
                  // Find the selected bank object
                  const selectedBankObj = bankAccounts.find(b => b.id === selectedBank);
                  if (!selectedBankObj) {
                    dispatch(showErroMessage('Selected bank not found'));
                    setUpdateBankLoading(false);
                    return;
                  }

                  // Prepare update payload
                  const updatePayload = {
                    destinationBankId: selectedBank,
                    destinationBank: selectedBankObj.bankName,
                    updateReason: bankComment
                  };

                  console.log('[PaymentApprovals] Updating bank details:', updatePayload);

                  // Use transactionId if available, otherwise use the topup request ID
                  const transactionIdForUpdate = selectedPayment.transactionNumber || selectedPayment.id;
                  
                  // Make API call to update the bank account for the transaction
                  const response = await AppService.put(
                    `/v1/admin/bank-transactions/${transactionIdForUpdate}/update-bank`,
                    updatePayload
                  );

                  if (response) {
                    console.log('[PaymentApprovals] Bank update successful');
                    dispatch(showSuccessMessage('Bank details updated successfully'));

                    // Close dialogs and reset state
                    setUpdateBankDialogOpen(false);
                    setSelectedBankValue('');
                    setBankComment('');
                    setDetailsDialogOpen(false);
                    setSelectedPayment(null);

                    // Reload pending approvals
                    loadPendingApprovals();
                  } else {
                    dispatch(showErroMessage('Failed to update bank details'));
                  }
                } catch (error) {
                  console.error('[PaymentApprovals] Error updating bank:', error);
                  dispatch(showErroMessage('Error updating bank details: ' + (error.message || 'Unknown error')));
                } finally {
                  setUpdateBankLoading(false);
                }
              }}
              variant="contained"
              startIcon={<EditIcon />}
              disabled={!selectedBank || !bankComment.trim() || updateBankLoading}
              sx={{
                bgcolor: '#0E6B6B',
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                py: 1,
                px: 3,
                '&:hover': {
                  bgcolor: '#0A5151',
                  boxShadow: '0 4px 12px rgba(14, 107, 107, 0.3)'
                },
                '&:disabled': {
                  bgcolor: '#BDBDBD',
                  color: '#ffffff'
                }
              }}
            >
              {updateBankLoading ? 'Updating...' : 'Update Bank'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
